import { z } from "zod";

/**
 * 🔴🔴 Contracts ĐẦU TƯ NAV — US-E20.F4-0210-S02 (@UC9334–@UC9338). ADR 0006.
 *
 * B1 NO-FLOAT ở BIÊN JSON: **tiền, NAV và SỐ ĐƠN VỊ đều là `string`** (BigInt tuần tự hoá) — không
 * bao giờ `number` (JSON number = IEEE-754 double ⇒ 1 BTC = 8 chữ số thập phân sẽ mất chính xác, và
 * số tiền lớn vượt 2^53). Mọi schema `.strict()` (dư field ⇒ reject).
 *
 * HAI THANG SỐ KHÁC NHAU (đừng lẫn — xem `@goppy/domain/invest`):
 *   · tiền/NAV : minor units (VND: 1 = 1 đồng). `navPerUnitMinor` = giá của MỘT ĐƠN VỊ NGUYÊN.
 *   · đơn vị   : `unitsScaled` = số đơn vị × 10^8 (UNIT_SCALE) ⇒ 100000000 ⇔ 1 đơn vị nguyên.
 *
 * 🔴 P/L có thể ÂM ⇒ dùng SIGNED string (`-?\d+`) — che lỗ bằng cách chỉ cho số dương là NÓI DỐI.
 */

/** Tiền/NAV/đơn vị KHÔNG âm (số nguyên chuỗi). */
const NON_NEGATIVE_INTEGER_STRING = /^\d+$/;
/** P/L CÓ DẤU — âm = đang lỗ (hiển thị sự thật). */
const SIGNED_INTEGER_STRING = /^-?\d+$/;

const positiveMoneyString = (label: string) =>
  z
    .string()
    .regex(NON_NEGATIVE_INTEGER_STRING, `${label} phải là số nguyên minor (chuỗi).`)
    .refine((v) => BigInt(v) > 0n, `${label} phải > 0.`);

/** Mã tài sản: BTC / VNM / SJC… (chữ HOA, số, dấu chấm/gạch — ≤ 20 ký tự). */
export const ASSET_SYMBOL_PATTERN = /^[A-Z0-9][A-Z0-9.-]{0,19}$/;
export const assetSymbolSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(ASSET_SYMBOL_PATTERN, "assetSymbol chỉ gồm A-Z 0-9 . - (≤20 ký tự).");

/** Nguồn NAV: manual (người nhập — CÓ AUDIT) | provider (tích hợp sàn — **DEFER**, chưa có producer). */
export const NAV_SOURCE_VALUES = ["manual", "provider"] as const;
export const navSourceSchema = z.enum(NAV_SOURCE_VALUES);

export const INVEST_POSITION_STATUS_VALUES = ["open", "closed"] as const;
export const investPositionStatusSchema = z.enum(INVEST_POSITION_STATUS_VALUES);

/* ─────────────────────────────── INPUT ─────────────────────────────── */

/** Mở vị thế đầu tư cho quỹ (1 quỹ có thể theo nhiều tài sản). */
export const openInvestPositionSchema = z
  .object({
    assetSymbol: assetSymbolSchema,
    /** NAV KHỞI ĐIỂM (giá 1 đơn vị lúc mở) — > 0. */
    initialNavPerUnitMinor: positiveMoneyString("initialNavPerUnitMinor"),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type OpenInvestPositionRequest = z.infer<typeof openInvestPositionSchema>;

/** @UC9334 — GÓP TIỀN mua đơn vị (số đơn vị tính theo NAV MỚI NHẤT — chống pha loãng). */
export const contributeInvestSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    amountMinor: positiveMoneyString("amountMinor"),
    idempotencyKey: z.string().trim().min(8).max(120),
  })
  .strict();
export type ContributeInvestRequest = z.infer<typeof contributeInvestSchema>;

/**
 * @UC9335 — GHI MỐC ĐỊNH GIÁ (NAV snapshot) — manual, CÓ AUDIT (ai nhập, khi nào).
 * 🔴 Ghi NAV **KHÔNG SINH BÚT TOÁN NÀO** (định giá lại = view). Xem ADR 0006.
 */
export const navSnapshotSchema = z
  .object({
    navPerUnitMinor: positiveMoneyString("navPerUnitMinor"),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type NavSnapshotRequest = z.infer<typeof navSnapshotSchema>;

/** @UC9337 — RÚT (bán) một phần theo NAV hiện hành. `unitsScaled` = số đơn vị × 10^8. */
export const withdrawInvestSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    unitsScaled: positiveMoneyString("unitsScaled"),
    idempotencyKey: z.string().trim().min(8).max(120),
  })
  .strict();
export type WithdrawInvestRequest = z.infer<typeof withdrawInvestSchema>;

/* ─────────────────────────────── RESPONSE ─────────────────────────────── */

export const investPositionResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    assetSymbol: z.string(),
    /** Tổng đơn vị ĐANG phát hành (×10^8) — Σ đơn vị mọi thành viên (bất biến bảo toàn @UC9338). */
    totalUnitsScaled: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    /** Σ giá vốn đang nắm giữ (minor) = số dư ASSET_HOLDING. KHÔNG phải giá thị trường. */
    costBasisMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    currency: z.string().length(3),
    status: investPositionStatusSchema,
  })
  .strict();
export type InvestPositionResponse = z.infer<typeof investPositionResponseSchema>;

/** Một LÔ đơn vị của thành viên (chống pha loãng: giữ NAV lúc vào để tính realized đúng lô — FIFO). */
export const investUnitResponseSchema = z
  .object({
    id: z.string().uuid(),
    positionId: z.string().uuid(),
    groupMemberId: z.string().uuid(),
    unitsScaled: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    navAtEntryMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    contributedMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
  })
  .strict();
export type InvestUnitResponse = z.infer<typeof investUnitResponseSchema>;

export const navSnapshotResponseSchema = z
  .object({
    id: z.string().uuid(),
    positionId: z.string().uuid(),
    navPerUnitMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    source: navSourceSchema,
    /** 🔴 AUDIT BẮT BUỘC: NAV manual dễ gian lận ⇒ luôn biết AI nhập, KHI NÀO. */
    enteredBy: z.string().uuid(),
    enteredAt: z.string().datetime(),
  })
  .strict();
export type NavSnapshotResponse = z.infer<typeof navSnapshotResponseSchema>;

/**
 * 🔴🔴 DÒNG DANH MỤC SAU ĐỊNH GIÁ LẠI — **VIEW THUẦN, 0 BÚT TOÁN**.
 * `unrealizedPnlMinor` SIGNED (âm = lỗ). Đây là LÃI TRÊN GIẤY, **KHÔNG PHẢI TIỀN MẶT**.
 */
export const investPnlViewSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    unitsScaled: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    costBasisMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    currentValueMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    unrealizedPnlMinor: z.string().regex(SIGNED_INTEGER_STRING),
  })
  .strict();
export type InvestPnlView = z.infer<typeof investPnlViewSchema>;

/** Toàn cảnh vị thế: NAV hiện hành + danh mục từng thành viên (view) + cảnh báo "lãi chưa thực hiện". */
export const investPositionViewResponseSchema = z
  .object({
    position: investPositionResponseSchema,
    /** NAV mới nhất (null ⇒ chưa có snapshot nào ⇒ chưa định giá được). */
    navPerUnitMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING).nullable(),
    navAt: z.string().datetime().nullable(),
    /** Σ giá trị thị trường (minor) — CHỈ ĐỂ HIỂN THỊ, KHÔNG phải số dư quỹ. */
    marketValueMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    /** Σ lãi/lỗ CHƯA thực hiện (signed) — 🔴 KHÔNG có trong sổ cái, không tiêu được. */
    unrealizedPnlMinor: z.string().regex(SIGNED_INTEGER_STRING),
    /** Σ CASH của quỹ (minor) — TIỀN THẬT, KHÔNG bao gồm giá trị tài sản. */
    fundCashMinor: z.string().regex(SIGNED_INTEGER_STRING),
    members: z.array(investPnlViewSchema),
  })
  .strict();
export type InvestPositionViewResponse = z.infer<typeof investPositionViewResponseSchema>;

/** Kết quả GÓP (mua đơn vị) — có `ledgerTxId` (INVEST_BUY) để đối chiếu sổ. */
export const contributeInvestResponseSchema = z
  .object({
    unit: investUnitResponseSchema,
    position: investPositionResponseSchema,
    navPerUnitMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    ledgerTxId: z.string().uuid(),
  })
  .strict();
export type ContributeInvestResponse = z.infer<typeof contributeInvestResponseSchema>;

/**
 * Kết quả RÚT (bán) — 🔴 hai bút toán TÁCH BẠCH:
 *   · `ledgerTxId`  : INVEST_SELL — HOÀN VỐN (DEBIT CASH giá vốn / CREDIT ASSET_HOLDING).
 *   · `pnlLedgerTxId`: OTHER_INCOME (lãi) hoặc EXPENSE (lỗ) — **BÚT TOÁN RIÊNG**, không lẫn vốn gốc.
 *     null khi bán đúng giá vốn (P/L = 0 ⇒ không có gì để ghi).
 */
export const withdrawInvestResponseSchema = z
  .object({
    positionId: z.string().uuid(),
    groupMemberId: z.string().uuid(),
    unitsSoldScaled: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    navPerUnitMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    /** Tiền THẬT về quỹ = giá vốn + lãi (hoặc − lỗ). */
    proceedsMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    costBasisMinor: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
    /** SIGNED — âm = bán lỗ. */
    realizedPnlMinor: z.string().regex(SIGNED_INTEGER_STRING),
    ledgerTxId: z.string().uuid(),
    pnlLedgerTxId: z.string().uuid().nullable(),
    remainingUnitsScaled: z.string().regex(NON_NEGATIVE_INTEGER_STRING),
  })
  .strict();
export type WithdrawInvestResponse = z.infer<typeof withdrawInvestResponseSchema>;

/**
 * CONFLICT-CODE-ROUND-3 (409, ratchet `conflict-code.invariants`, @UC9334) — quỹ đã có vị thế cho tài sản này
 * (unique(fundId, assetSymbol)). `params.assetSymbol` = mã tài sản người dùng vừa gửi (không tiền, không PII).
 * Góp / bán trùng khoá ⇒ `LEDGER_CONCURRENT_WRITE` (ledger-write.ts) — KHÔNG mã riêng vùng đầu tư.
 */
export const INVEST_POSITION_ALREADY_OPEN_CODE = "INVEST_POSITION_ALREADY_OPEN" as const;
